import { useState, useRef, useEffect } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { TEMPLATE_TAGS } from '@/lib/tags';

interface TagSelectProps {
    value: string[];
    onChange: (tags: string[]) => void;
    placeholder?: string;
}

export function TagSelect({ value, onChange, placeholder = 'Select tags...' }: TagSelectProps) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const ref = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setOpen(false);
                setSearch('');
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    const filtered = TEMPLATE_TAGS.filter(
        (tag) => !value.includes(tag) && tag.toLowerCase().includes(search.toLowerCase())
    );

    const toggle = (tag: string) => {
        if (value.includes(tag)) {
            onChange(value.filter((t) => t !== tag));
        } else {
            onChange([...value, tag]);
            setSearch('');
        }
    };

    const remove = (tag: string) => {
        onChange(value.filter((t) => t !== tag));
    };

    return (
        <div className="relative" ref={ref}>
            <div
                className="flex min-h-[38px] cursor-pointer flex-wrap items-center gap-1 rounded-md border border-slate-700 bg-slate-800 px-2 py-1.5"
                onClick={() => {
                    setOpen(true);
                    inputRef.current?.focus();
                }}
            >
                {value.map((tag) => (
                    <span
                        key={tag}
                        className="flex items-center gap-1 rounded bg-violet-900/50 px-1.5 py-0.5 text-xs text-violet-300"
                    >
                        {tag}
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                remove(tag);
                            }}
                            className="text-violet-400 hover:text-violet-200"
                        >
                            <X className="h-3 w-3" />
                        </button>
                    </span>
                ))}
                <input
                    ref={inputRef}
                    value={search}
                    onChange={(e) => {
                        setSearch(e.target.value);
                        setOpen(true);
                    }}
                    onFocus={() => setOpen(true)}
                    placeholder={value.length === 0 ? placeholder : ''}
                    className="min-w-[80px] flex-1 bg-transparent text-sm text-slate-100 placeholder:text-slate-500 outline-none"
                />
                <ChevronDown className={`h-4 w-4 shrink-0 text-slate-500 transition-transform ${open ? 'rotate-180' : ''}`} />
            </div>

            {open && (
                <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-48 overflow-y-auto rounded-md border border-slate-700 bg-slate-900 py-1 shadow-xl">
                    {filtered.length === 0 ? (
                        <p className="px-3 py-2 text-xs text-slate-500">
                            {search ? 'No matching tags.' : 'All tags selected.'}
                        </p>
                    ) : (
                        filtered.map((tag) => (
                            <button
                                key={tag}
                                type="button"
                                onClick={() => toggle(tag)}
                                className="flex w-full items-center px-3 py-1.5 text-left text-sm text-slate-300 hover:bg-slate-800"
                            >
                                {tag}
                            </button>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}
